/* eslint-disable react/jsx-filename-extension */
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Backr } from "./styles";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../services/api";

interface Props {
  role: string;
}

interface TenantInfo {
  empresa: {
    ativo: boolean;
    cgc: string;
    codigo: number;
    fantasia: string | null;
    nome: string;
  };
  contrato: {
    ativo: boolean;
    emailresponsavel: string;
    empresa_codigo: number;
    modulo_financeiro: boolean;
    planocontratado: string;
    qtdepacientes: number;
    representante_comercial: string;
    representante_cs1: string;
    responsavel: string;
  };
}

interface Clinic {
  id: number;
  clinica: string;
}

const TenantInformation: React.FC<Props> = ({ role }) => {
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const { handleSubmit, setValue, watch } = useForm();
  const companyInputValue = watch("companyInput", "");

  useEffect(() => {
    async function loadClinics() {
      const response = await api.get(`/clinicas`);
      setClinics(response.data);
    }
    loadClinics();
  }, []);

  const mascaraCNPJ = (value: string) => {
    let cnpj = value.replace(/\D/g, "");
    cnpj = cnpj.replace(/^(\d{2})(\d)/g, "$1.$2");
    cnpj = cnpj.replace(/(\d{3})(\d)/, "$1.$2");
    cnpj = cnpj.replace(/(\d{3})(\d)/, "$1/$2");
    cnpj = cnpj.replace(/(\d{4})(\d)/, "$1-$2");
    return cnpj;
  };

  const handleSearch = async (data: any) => {
    try {
      let inputValue = data.companyInput;
      let cgcValue = "";
      const foundClinic = clinics.find(
        (clinic) =>
          String(clinic.id) === inputValue ||
          `${clinic.id} - ${clinic.clinica}`.toLowerCase() === inputValue.toLowerCase()
      );
      if (foundClinic) {
        cgcValue = String(foundClinic.id);
      } else {
        // Not found, treat as CNPJ and apply mask
        cgcValue = mascaraCNPJ(inputValue);
        if (cgcValue.length !== 18) {
          toast.error("CNPJ inválido ou empresa não encontrada.");
          return;
        }
      }
      const response = await api.get(`/tenant_info?cgc=${cgcValue}`);
      setTenantInfo(response.data);
      setShowModal(true);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao buscar informações da empresa");
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setTenantInfo(null);
  };

  if (!role) return null;

  return (
    <div>
      <Backr>
        <h1>Informações Empresas</h1>
        <Form onSubmit={handleSubmit(handleSearch)}>
          <Row className="mb-3 align-items-center">
            <Col md={8}>
              <Form.Group className="input-area">
                <Form.Label>Empresa (id - nome) ou CNPJ</Form.Label>
                <Form.Control
                  type="text"
                  id="companyInput"
                  list="companyList"
                  value={companyInputValue}
                  placeholder="Selecione a empresa ou digite o CNPJ"
                  onChange={(e) => {
                    const val = e.target.value;
                    const isClinic = clinics.some(
                      (clinic) =>
                        `${clinic.id} - ${clinic.clinica}`.toLowerCase() === val.toLowerCase()
                    );
                    if (!isClinic && /^\d+$/.test(val.replace(/\D/g, ""))) {
                      setValue("companyInput", mascaraCNPJ(val));
                    } else {
                      setValue("companyInput", val);
                    }
                  }}
                />
                <datalist id="companyList">
                  {clinics.map((clinic) => (
                    <option key={clinic.id} value={`${clinic.id} - ${clinic.clinica}`} />
                  ))}
                </datalist>
              </Form.Group>
            </Col>
            <Col xs={4} className="mt-3">
              <Button type="submit">Buscar</Button>
            </Col>
          </Row>
        </Form>

        <Modal show={showModal} onHide={handleClose} size="lg">
          <Modal.Header>
            <Modal.Title>Informações da Empresa</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {tenantInfo && (
              <Row>
                <Col md={6}>
                  <h5>Empresa</h5>
                  <p>
                    <strong>Nome:</strong> {tenantInfo.empresa.codigo} - {tenantInfo.empresa.nome}
                  </p>
                  <p>
                    <strong>Fantasia:</strong> {tenantInfo.empresa.fantasia}
                  </p>
                  <p>
                    <strong>CGC:</strong> {tenantInfo.empresa.cgc}
                  </p>
                  <p>
                    <strong>Status da Base:</strong>{" "}
                    {tenantInfo.empresa.ativo ? "Ativa" : "Inativa"}
                  </p>
                </Col>
                <Col md={6}>
                  <h5>Contrato</h5>
                  <p>
                    <strong>Responsável:</strong> {tenantInfo.contrato.responsavel}
                  </p>
                  <p>
                    <strong>Email:</strong> {tenantInfo.contrato.emailresponsavel}
                  </p>
                  <p>
                    <strong>Plano:</strong> {tenantInfo.contrato.planocontratado}
                  </p>
                  <p>
                    <strong>Módulo Financeiro:</strong>{" "}
                    {tenantInfo.contrato.modulo_financeiro ? "Ativo" : "Inativo"}
                  </p>
                  <p>
                    <strong>Quantidade de Pacientes:</strong> {tenantInfo.contrato.qtdepacientes}
                  </p>
                  <p>
                    <strong>Vendedor:</strong>{" "}
                    {tenantInfo.contrato.representante_comercial || "Não informado"}
                  </p>
                  <p>
                    <strong>CS:</strong> {tenantInfo.contrato.representante_cs1 || "Não informado"}
                  </p>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleClose}>Fechar</Button>
          </Modal.Footer>
        </Modal>
      </Backr>
    </div>
  );
};

export default TenantInformation;
